import { DEFAULT_LOOK, TANK_BODIES, TANK_DECALS, type TankLook } from '@tanky/shared';
import { api } from '../api';
import { h, toast } from '../dom';
import { session } from '../session';
import { drawTank, emblemImage, putEmblem } from '../game/tankArt';

const COLORS = ['#e74c3c', '#ff7a3d', '#f1c40f', '#8bc34a', '#2ecc71', '#1abc9c', '#3498db', '#3f51b5', '#9b59b6', '#e91e8c', '#8d6e63', '#ecf0f1'];
const ACCENTS = ['#ffe08a', '#ffffff', '#111111', '#ff5c6c', '#4cd18a', '#5ab8ff'];

/** Editor vzhledu tanku s živým náhledem. */
export function tankEditor(onSaved?: (look: TankLook) => void): { el: HTMLElement; destroy(): void } {
  const saved: TankLook = session.look ?? { ...DEFAULT_LOOK };
  let look: TankLook = { ...saved };

  const preview = h('canvas', { class: 'tank-preview' });
  const saveBtn = h('button', { class: 'primary', type: 'button' }, 'Uložit tank');
  const dirtyNote = h('span', { class: 'muted small' });

  const bodyRow = h('div', { class: 'chips' });
  const decalRow = h('div', { class: 'chips' });
  const colorRow = h('div', { class: 'swatches' });
  const accentRow = h('div', { class: 'swatches' });

  // --- Obrázek na tanku ---
  const me = session.me!.id;
  let emblemImg: HTMLImageElement | null = null;
  const emblemPreview = h('div', { class: 'emblem-preview' });
  const fileInput = h('input', { type: 'file', accept: 'image/png,image/jpeg,image/webp,image/gif', hidden: true }) as HTMLInputElement;
  const uploadBtn = h('button', { type: 'button', onclick: () => fileInput.click() }, '📷 Nahrát obrázek');
  const removeBtn = h('button', { type: 'button', class: 'ghost danger' }, 'Odebrat');

  function renderEmblem() {
    emblemImg = look.emblem ? emblemImage(me, look) ?? emblemImg : null;
    emblemPreview.replaceChildren(emblemImg ? emblemImg.cloneNode() : h('span', { class: 'muted' }, '—'));
    removeBtn.hidden = !look.emblem;
    uploadBtn.textContent = look.emblem ? '📷 Změnit obrázek' : '📷 Nahrát obrázek';
  }

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (!file) return;
    uploadBtn.disabled = true;
    try {
      const { dataUrl, img } = await squareImage(file, 96);
      const r = await api.uploadEmblem(dataUrl);
      putEmblem(me, r.look.emblem!, img);
      emblemImg = img;
      session.look = { ...(session.look ?? DEFAULT_LOOK), emblem: r.look.emblem };
      look = { ...look, emblem: r.look.emblem };
      render();
      toast('Obrázek nahrán – na tanku je vidět při velkém přiblížení.');
    } catch (e) {
      toast((e as Error).message, true);
    } finally {
      uploadBtn.disabled = false;
    }
  });
  removeBtn.addEventListener('click', async () => {
    try {
      await api.deleteEmblem();
      if (session.look) delete session.look.emblem;
      delete look.emblem;
      emblemImg = null;
      render();
    } catch (e) {
      toast((e as Error).message, true);
    }
  });

  function swatches(row: HTMLElement, list: string[], key: 'color' | 'accent') {
    const custom = h('input', { type: 'color', value: look[key], title: 'Vlastní barva' }) as HTMLInputElement;
    custom.addEventListener('input', () => set({ [key]: custom.value }));
    row.replaceChildren(
      ...list.map((c) =>
        h('button', {
          type: 'button',
          class: `sw${look[key] === c ? ' sel' : ''}`,
          style: { background: c },
          title: c,
          'aria-label': c,
          onclick: () => set({ [key]: c }),
        }),
      ),
      h('label', { class: `sw custom${list.includes(look[key]) ? '' : ' sel'}`, title: 'Vlastní barva' }, custom, '🎨'),
    );
  }

  function render() {
    bodyRow.replaceChildren(
      ...TANK_BODIES.map((b) =>
        h('button', { type: 'button', class: look.body === b.id ? 'sel' : '', onclick: () => set({ body: b.id }) }, b.name),
      ),
    );
    decalRow.replaceChildren(
      ...TANK_DECALS.map((d) =>
        h('button', { type: 'button', class: look.decal === d.id ? 'sel' : '', onclick: () => set({ decal: d.id }) }, d.name),
      ),
    );
    swatches(colorRow, COLORS, 'color');
    swatches(accentRow, ACCENTS, 'accent');
    const dirty = JSON.stringify(look) !== JSON.stringify(session.look ?? DEFAULT_LOOK) || !session.look;
    saveBtn.disabled = !dirty;
    dirtyNote.textContent = dirty ? '' : 'Uloženo ✓';
    renderEmblem();
  }

  function set(patch: Partial<TankLook>) {
    look = { ...look, ...patch };
    render();
  }

  saveBtn.addEventListener('click', async () => {
    saveBtn.disabled = true;
    try {
      const r = await api.saveLook(look);
      session.look = r.look;
      look = { ...r.look };
      render();
      onSaved?.(r.look);
      toast('Tank uložen – projeví se v nově spuštěných hrách.');
    } catch (e) {
      toast((e as Error).message, true);
      render();
    }
  });

  // Animovaný náhled
  const ctx = preview.getContext('2d')!;
  let raf = 0;
  let destroyed = false;
  const draw = (t: number) => {
    if (destroyed) return;
    raf = requestAnimationFrame(draw);
    const w = preview.clientWidth || 220;
    const hh = preview.clientHeight || 110;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (preview.width !== Math.round(w * dpr)) {
      preview.width = Math.round(w * dpr);
      preview.height = Math.round(hh * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const g = ctx.createLinearGradient(0, 0, 0, hh);
    g.addColorStop(0, '#1c2748');
    g.addColorStop(1, '#8a5a6a');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, hh);
    ctx.fillStyle = '#5a9c45';
    ctx.fillRect(0, hh - 22, w, 4);
    ctx.fillStyle = '#7a5034';
    ctx.fillRect(0, hh - 18, w, 18);
    const k = Math.min(3.4, (hh - 30) / 20);
    ctx.setTransform(dpr * k, 0, 0, dpr * k, (w / 2) * dpr, (hh - 22) * dpr);
    const angle = 90 + Math.sin(t / 900) * 55;
    if (!emblemImg && look.emblem) emblemImg = emblemImage(me, look);
    drawTank(ctx, 0, 0, angle, look, look.color, { alive: true, detail: true, emblem: emblemImg, wind: 30, time: t / 1000 });
  };
  raf = requestAnimationFrame(draw);

  render();

  const el = h(
    'div',
    { class: 'tank-editor' },
    preview,
    h('div', { class: 'field' }, h('span', { class: 'lbl' }, 'Typ'), bodyRow),
    h('div', { class: 'field' }, h('span', { class: 'lbl' }, 'Barva'), colorRow),
    h('div', { class: 'field' }, h('span', { class: 'lbl' }, 'Doplňková barva'), accentRow),
    h('div', { class: 'field' }, h('span', { class: 'lbl' }, 'Obtisk'), decalRow),
    h(
      'div',
      { class: 'field' },
      h('span', { class: 'lbl' }, 'Obrázek na tanku (nahradí obtisk; ve hře je vidět při velkém přiblížení)'),
      h('div', { class: 'row' }, emblemPreview, uploadBtn, removeBtn, fileInput),
    ),
    h('p', { class: 'muted small', style: { margin: '0' } }, 'Typ tanku je jen vzhled – všechny tanky jsou ve hře stejně silné.'),
    h('div', { class: 'row' }, saveBtn, dirtyNote),
  );

  return {
    el,
    destroy() {
      destroyed = true;
      cancelAnimationFrame(raf);
    },
  };
}

/** Ořízne obrázek na čtverec (střed), zmenší a vrátí data URL do 48 kB. */
async function squareImage(file: File, size: number): Promise<{ dataUrl: string; img: HTMLImageElement }> {
  if (file.size > 15 * 1024 * 1024) throw new Error('Soubor je moc velký.');
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('Tenhle soubor neumím přečíst jako obrázek.');
  });
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const side = Math.min(bitmap.width, bitmap.height);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
  bitmap.close();
  let dataUrl = canvas.toDataURL('image/webp', 0.85);
  if (!dataUrl.startsWith('data:image/webp')) dataUrl = canvas.toDataURL('image/png');
  if (dataUrl.length > 60_000) dataUrl = canvas.toDataURL('image/jpeg', 0.8);
  const img = new Image();
  img.src = dataUrl;
  await img.decode().catch(() => {});
  return { dataUrl, img };
}
